import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram, Youtube } from 'lucide-react';
import { contactAPI, websiteSettingsAPI } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';

const Footer = styled.footer`
  background-color: ${props => props.theme.colors.text.primary};
  color: ${props => props.theme.colors.white};
  padding: 3rem 2rem 0.5rem 2rem;
  margin-top: auto;
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  margin-bottom: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FooterSection = styled.div`
  h3 {
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.semibold};
    margin: 0 0 1rem 0;
    font-family: ${props => props.theme.typography.fontFamily.heading};
  }
`;

const FooterText = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  line-height: 1.6;
  opacity: 0.9;
  margin: 0 0 1rem 0;
`;

const FooterLink = styled.button`
  display: block;
  background: none;
  border: none;
  color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.typography.fontSize.sm};
  text-align: left;
  padding: 0.5rem 0;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  opacity: 0.9;
  
  svg {
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.full};
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1.5rem;
  text-align: center;
  font-size: ${props => props.theme.typography.fontSize.sm};
  opacity: 0.8;
  p { margin: 0; }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const FooterLogoIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  background-color: ${props => props.hasLogo ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  
  span {
    color: ${props => props.theme.colors.white};
    font-size: ${props => props.theme.typography.fontSize.lg};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
  }
`;

const PublicFooter = ({ theme: themeProp }) => {
  const { theme: themeContext } = useTheme();
  const theme = themeProp || themeContext;
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [websiteSettings, setWebsiteSettings] = useState(null);

  useEffect(() => {
    fetchContactSettings();
    fetchWebsiteSettings();
  }, []);

  const fetchWebsiteSettings = async () => {
    try {
      const response = await websiteSettingsAPI.getSettings();
      if (response.data) {
        console.log('Footer - Website settings:', response.data);
        setWebsiteSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching website settings in footer:', error);
    }
  };

  const fetchContactSettings = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      // Don't set settings if there's an error - let it show nothing
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (path) => {
    // Handle hash links for landing page sections
    if (path.startsWith('#')) {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(path);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      navigate(path);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return <Facebook size={20} />;
      case 'twitter':
        return <Twitter size={20} />;
      case 'linkedin':
        return <Linkedin size={20} />;
      case 'instagram':
        return <Instagram size={20} />;
      case 'youtube':
        return <Youtube size={20} />;
      default:
        return null;
    }
  };

  return (
    <Footer theme={theme}>
      <FooterContent>
        <FooterGrid>
          <FooterSection theme={theme}>
            <FooterLogo>
              {websiteSettings?.logo_path ? (
                <FooterLogoIcon theme={theme} hasLogo={true}>
                  <img 
                    src={websiteSettings.logo_path} 
                    alt={websiteSettings.website_name || "EWHRDN Logo"}
                    onError={(e) => {
                      console.error('Footer logo failed to load:', websiteSettings.logo_path);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Footer logo loaded:', websiteSettings.logo_path);
                    }}
                  />
                </FooterLogoIcon>
              ) : (
                <FooterLogoIcon theme={theme} hasLogo={false}>
                  <span>E</span>
                </FooterLogoIcon>
              )}
              <div>
                <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg }}>
                  {websiteSettings?.website_name || 'EWHRDN'}
                </h3>
              </div>
            </FooterLogo>
            <FooterText theme={theme}>
              Ethiopian Women Human Rights Defenders Network
            </FooterText>
            <FooterText theme={theme}>
              Empowering women, defending rights, creating change.
            </FooterText>
          </FooterSection>

          <FooterSection theme={theme}>
            <h3>Quick Links</h3>
            <FooterLink theme={theme} onClick={() => handleNavigation('/about')}>
              About Us
            </FooterLink>
            <FooterLink theme={theme} onClick={() => handleNavigation('/news')}>
              News
            </FooterLink>
            <FooterLink theme={theme} onClick={() => handleNavigation('/events')}>
              Events
            </FooterLink>
            <FooterLink theme={theme} onClick={() => handleNavigation('/publications')}>
              Publications
            </FooterLink>
            <FooterLink theme={theme} onClick={() => handleNavigation('/gallery')}>
              Gallery
            </FooterLink>
          </FooterSection>

          <FooterSection theme={theme}>
            <h3>Contact Info</h3>
            {loading ? (
              <FooterText theme={theme} style={{ opacity: 0.6 }}>
                Loading...
            </FooterText>
            ) : settings ? (
              <>
                {/* Address */}
                {settings.address && (
                  <ContactItem theme={theme}>
                    <MapPin size={18} />
                    <div>
                      {settings.address}
                      {settings.po_box && `, P.O. Box ${settings.po_box}`}
                      {settings.city && `, ${settings.city}`}
                      {settings.country && `, ${settings.country}`}
                      {settings.landmark && ` (${settings.landmark})`}
                    </div>
                  </ContactItem>
                )}
                
                {/* Phone Numbers */}
                {settings.phone_numbers && settings.phone_numbers.length > 0 && (
                  settings.phone_numbers.map((phone, index) => (
                    <ContactItem key={index} theme={theme}>
                      <Phone size={18} />
                      <div>
                        <a href={`tel:${phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {phone}
                        </a>
                      </div>
                    </ContactItem>
                  ))
                )}
                
                {/* Emails */}
                {settings.emails && settings.emails.length > 0 && (
                  settings.emails.map((email, index) => (
                    <ContactItem key={index} theme={theme}>
                      <Mail size={18} />
                      <div>
                        <a href={`mailto:${email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                          {email}
                        </a>
                      </div>
                    </ContactItem>
                  ))
                )}
                
                {/* Office Hours */}
                {settings.office_hours && (
                  <ContactItem theme={theme}>
                    <div style={{ fontSize: theme?.typography?.fontSize?.sm || '0.875rem', opacity: 0.9 }}>
                      {settings.office_hours}
                    </div>
                  </ContactItem>
                )}
                
                {/* Show message if no contact info is configured */}
                {!settings.address && 
                 (!settings.phone_numbers || settings.phone_numbers.length === 0) && 
                 (!settings.emails || settings.emails.length === 0) && 
                 !settings.office_hours && (
                  <FooterText theme={theme} style={{ opacity: 0.6, fontStyle: 'italic' }}>
                    Contact information will appear here
            </FooterText>
                )}
              </>
            ) : (
              <FooterText theme={theme} style={{ opacity: 0.6, fontStyle: 'italic' }}>
                Contact information will appear here
            </FooterText>
            )}
          </FooterSection>

          <FooterSection theme={theme}>
            <h3>Follow Us</h3>
            <FooterText theme={theme}>
              Stay connected with our latest updates
            </FooterText>
            {loading ? (
              <div style={{ opacity: 0.6, padding: '1rem 0' }}>
                Loading social media links...
              </div>
            ) : settings && settings.social_media && Object.keys(settings.social_media).length > 0 ? (
            <SocialLinks>
                {Object.entries(settings.social_media).map(([platform, url]) => 
                  url && (
                    <SocialLink
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={platform}
                      theme={theme}
                    >
                      {getSocialIcon(platform)}
              </SocialLink>
                  )
                )}
            </SocialLinks>
            ) : (
              <FooterText theme={theme} style={{ opacity: 0.6, fontStyle: 'italic', marginTop: '1rem' }}>
                Social media links will appear here
              </FooterText>
            )}
          </FooterSection>
        </FooterGrid>

        <FooterBottom theme={theme}>
          <p>© {new Date().getFullYear()} Ethiopian Women Human Rights Defenders Network. All rights reserved.</p>
        </FooterBottom>
      </FooterContent>
    </Footer>
  );
};

export default PublicFooter;

